import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Button from 'components/field/Button';
import PasswordInput from 'components/field/PasswordInput';
import FormItem from 'components/form/FormItem';
import * as F from 'components/form/FormLayout.styled';
import { FormFooter } from 'components/form/FormLayout.styled';
import FormHeader from 'components/global/FormHeader';
import Icon from 'components/global/Icon';
import Tag from 'components/global/Tag';
import Typography from 'components/global/Typography';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMediaQuery from 'hooks/useMediaQuery';
import { usePrompt } from 'hooks/usePrompt';

import { PROFILE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

import { faCircleCheck, faUnlockAlt } from '@fortawesome/pro-solid-svg-icons';

import * as S from './ProfileSettings.styled';
import TwoFactorDisableModal from './TwoFactorDisableModal';
import TwoFactorEnableModal from './TwoFactorEnableModal';
import TwoFactorRegenerateModal from './TwoFactorRegenerateModal';

const defaultColSpan = {
	lg: 8,
	xl: 7
};

const SecuritySettings = () => {
	const { t } = useTranslation();
	const axios = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();
	const { hasPassword, mfaTypes } = useSelector((state) => state?.user);
	const hasTwoFactor = mfaTypes === 'totp';
	const isMobileLandscape = useMediaQuery(Breakpoints.mobileLandscape);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					...(hasPassword ? { currentPassword: yup.string().nullable().required() } : {}),
					password: yup
						.string()
						.matches(/^(?=.{8,})/, t('passwordError.8Character'))
						.matches(/^(?=.*[a-z])/, t('passwordError.lowerCase'))
						.matches(/^(?=.*[A-Z])/, t('passwordError.upperCase'))
						.matches(/^(?=.*\d)/, t('passwordError.oneNumber'))
						.nullable()
						.required(),
					confirmPassword: yup
						.string()
						.oneOf([yup.ref('password'), null], t('passwordError.mustMatch'))
						.nullable()
						.required()
				})
				.required(),
		[hasPassword]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			currentPassword: null,
			password: null,
			confirmPassword: null
		}
	});

	const {
		handleSubmit,
		watch,
		reset,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const onSubmit = async (data) => {
		try {
			const response = await axios.post(PROFILE.UPDATE_PASSWORD, {
				...data,
				confirmPassword: undefined
			});
			if (response.status === 200) {
				reset();
				showSuccess({
					message: 'alert.passwordChangeSuccessfully'
				});
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			const {
				response: {
					data: { code }
				}
			} = error;
			if (code === 'error.B012') {
				reset();
			}
			showError({
				message: code
			});
		}
	};

	const password = watch('password');

	const validation = useMemo(
		() => ({
			upperCase: password?.match(/[A-Z]/) ?? false,
			lowerCase: password?.match(/[a-z]/) ?? false,
			number: password?.match(/\d/) ?? false,
			length: password?.length >= 8
		}),
		[password]
	);

	return (
		<div>
			<HeaderContainerFormHeader className="settingsMaxWidth settingsFormHeader">
				<FormHeader boldTitle="securitySettings" />
				<S.ButtonContainer>
					<Button
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
						className="saveButton"
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						icon={faUnlockAlt}
						label="changePassword"
						variant="blue"
						disabled={!isDirty || isSubmitting}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
					/>
				</S.ButtonContainer>
			</HeaderContainerFormHeader>
			<S.StyledSecurityForm formMethods={formMethods} borderless>
				<S.StyledCard className="settingsMaxWidth" fullWidth>
					{hasPassword && (
						<FormItem colSpan={defaultColSpan} label="currentPassword" isLoading={isSubmitting} required>
							<PasswordInput name="currentPassword" />
						</FormItem>
					)}
					<FormItem colSpan={defaultColSpan} label="newPassword" isLoading={isSubmitting} required>
						<PasswordInput name="password" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="newPasswordAgain" isLoading={isSubmitting} required>
						<PasswordInput name="confirmPassword" />
					</FormItem>
					<S.SecurityRequirementsWrapper className="security-requirements">
						<S.SecurityRequirementsContainer>
							{['length', 'number', 'upperCase', 'lowerCase'].map((item, index) => (
								<S.SecurityRequirementsItem key={index}>
									<Icon
										icon={faCircleCheck}
										size="medium"
										color={validation[item] ? 'var(--blue)' : 'var(--content-4)'}
									/>
									<Typography>{`passwordRequirements.${item}`}</Typography>
								</S.SecurityRequirementsItem>
							))}
						</S.SecurityRequirementsContainer>
					</S.SecurityRequirementsWrapper>
				</S.StyledCard>
			</S.StyledSecurityForm>

			<S.TwoFactorCard className="settingsMaxWidth" fullWidth>
				<S.TwoFactorSettingsSection>
					<S.TwoFactorCardContainer>
						<S.TwoFactorCardTitle>
							<Typography variant="p2" fontWeight="500">
								securitySettings.twoFactorAuthHeader
							</Typography>
							{hasTwoFactor && <Tag label="twoFactorEnabledTag" variant="active" />}
						</S.TwoFactorCardTitle>

						<Typography color="var(--content-3)" noWrap>
							securitySettings.twoFactorAuthDescription
						</Typography>
					</S.TwoFactorCardContainer>
					{hasTwoFactor ? (
						<S.TwoFactorDisableButton
							label="disableTwoFactor"
							variant="outline"
							onClick={() => {
								NiceModal.show(TwoFactorDisableModal);
							}}
							size="x-large"
						/>
					) : (
						<Button
							label="enableTwoFactor"
							variant="outline"
							onClick={() => {
								NiceModal.show(TwoFactorEnableModal);
							}}
							size="x-large"
							disabled={!hasPassword}
							disabledTooltip="securitySettings.enableTwoFactorHasNoPassword"
						/>
					)}
				</S.TwoFactorSettingsSection>
				{hasTwoFactor && (
					<>
						<F.Line className="line" />
						<S.TwoFactorSettingsSection>
							<S.TwoFactorCardContainer>
								<S.TwoFactorCardTitle>
									<Typography variant="p2" fontWeight="500">
										securitySettings.backupVerificationCodesTitle
									</Typography>
								</S.TwoFactorCardTitle>

								<Typography color="var(--content-3)" noWrap>
									securitySettings.backupVerificationCodesDescription
								</Typography>
							</S.TwoFactorCardContainer>
							<Button
								label={isMobileLandscape ? 'regenerate' : 'regenerateBackupCodes'}
								variant="outline"
								size="x-large"
								onClick={() => NiceModal.show(TwoFactorRegenerateModal)}
							/>
						</S.TwoFactorSettingsSection>
					</>
				)}
			</S.TwoFactorCard>

			<FormFooter>
				<Button
					lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/save-icon.json.gz"
					className="saveButton"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					icon={faUnlockAlt}
					label="changePassword"
					variant="blue"
					disabled={!isDirty || isSubmitting}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
				/>
			</FormFooter>
		</div>
	);
};

export default SecuritySettings;
